import { isEmpty } from "lodash-es";

const suffixes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

export const getBytes = (bytes: number) => {
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (
    (!bytes && "0 Bytes") || `${(bytes / 1024 ** i).toFixed(2)} ${suffixes[i]}`
  );
};

export const getFileExtension = (contentUri = ""): string => {
  if (isEmpty(contentUri)) return "";
  const normalizedInput = contentUri.trim();
  const extractExtension = (value: string): string => {
    const lastSegment = value.split(/[?#]/)[0]?.split("/").pop() || "";
    const lastDotIndex = lastSegment.lastIndexOf(".");

    if (lastDotIndex <= 0 || lastDotIndex === lastSegment.length - 1) {
      return "";
    }

    return lastSegment.slice(lastDotIndex + 1).toLocaleLowerCase();
  };

  try {
    const uri = normalizedInput.includes("://")
      ? new URL(normalizedInput)
      : null;
    const path = uri?.pathname || normalizedInput;

    return extractExtension(path);
  } catch {
    return extractExtension(normalizedInput);
  }
};

export const resolveAttachmentFileExtension = (options: {
  fileName?: string | null;
  contentUri?: string | null;
  contentType?: string | null;
}): string => {
  return (
    getFileExtension(options.fileName || "") ||
    getFileExtension(options.contentUri || "") ||
    getFileExtensionFromMimeType(options.contentType || "")
  );
};

export const getFileExtensionFromMimeType = (mimeType = "") => {
  const normalizedMimeType = mimeType.toLowerCase().trim();

  switch (normalizedMimeType) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/gif":
      return "gif";
    case "image/webp":
      return "webp";
    case "image/heic":
      return "heic";
    case "image/heif":
      return "heif";
    case "video/mp4":
      return "mp4";
    case "video/quicktime":
      return "mov";
    case "application/pdf":
      return "pdf";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";
    case "application/msword":
      return "doc";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "xlsx";
    case "application/vnd.ms-excel":
      return "xls";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "pptx";
    case "application/vnd.ms-powerpoint":
      return "ppt";
    case "application/zip":
      return "zip";
    default:
      return "";
  }
};

export const isImage = (fileExtension: string): boolean => {
  return (
    [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "heif",
      "heic",
      "tiff",
      "webp",
    ].indexOf(fileExtension) >= 0
  );
};

/**
 * Can an `<img>` in this browser actually PAINT these bytes?
 *
 * Deliberately narrower than {@link isImage}, and the distinction is load-bearing:
 * `isImage` answers "is this an image FORMAT", which is not the same question. HEIC —
 * what every iPhone photo is — decodes only in Safari, and TIFF decodes nowhere; both
 * are images, and both render the broken-image glyph. Anything this returns `false` for
 * needs a server-side rendition (Birdie's JPEG derivative) before it can be shown, so
 * local original bytes must never be offered as its preview.
 *
 * HEIC is excluded even though Safari CAN paint it: the derivative works in every
 * browser, and one uniform path is worth more than saving Safari a small fetch.
 */
export const isBrowserPaintableImage = (fileExtension: string): boolean => {
  return ["jpg", "jpeg", "png", "gif", "bmp", "webp", "avif"].includes(
    fileExtension,
  );
};

export const isVideo = (fileExtension: string): boolean => {
  return ["mp4", "mov", "avi", "hevc", "mkv"].indexOf(fileExtension) >= 0;
};

/** By EXTENSION only — a `.webm` may be audio or video; check the MIME type first. */
export const isAudio = (fileExtension: string): boolean => {
  return [
    "mp3",
    "m4a",
    "wav",
    "ogg",
    "oga",
    "aac",
    "flac",
    "opus",
    "weba",
  ].includes(fileExtension);
};

export const isPdf = (fileExtension: string): boolean => {
  return ["pdf"].indexOf(fileExtension) >= 0;
};

export const isRtf = (fileExtension: string): boolean => {
  return ["rtf"].indexOf(fileExtension) >= 0;
};

export const isDocument = (fileExtension: string): boolean => {
  return ["doc", "docx"].indexOf(fileExtension) >= 0;
};

export const isArchive = (fileExtension: string): boolean => {
  return ["zip", "gz", "tar", "rar"].indexOf(fileExtension) >= 0;
};

export const isSheet = (fileExtension: string): boolean => {
  return ["xls", "xlsx"].indexOf(fileExtension) >= 0;
};

export const isPpt = (fileExtension: string): boolean => {
  return ["ppt", "pptx"].indexOf(fileExtension) >= 0;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return "0 B";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

/**
 * "report.final.pdf" → `{ base: "report.final", extension: ".pdf" }` — the
 * design sets the extension apart so a long name truncates before it. A name
 * with no dot, or only a leading one (".env"), has no extension.
 */
export function splitFileName(fileName: string): {
  base: string;
  extension: string;
} {
  const dot = fileName.lastIndexOf(".");
  if (dot <= 0) return { base: fileName, extension: "" };
  return { base: fileName.slice(0, dot), extension: fileName.slice(dot) };
}
